import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, DateRangePicker } from '../components/common';
import { ROUTES } from '../constants';
import { shopperService, ApiError } from '../services';

const formatNumber = value => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '0';
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 10,
  }).format(number);
};

const ShoppersPage = () => {
  const navigate = useNavigate();
  const hasInitialLoadRef = useRef(false);
  const messageBannerRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [shoppers, setShoppers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [exportResult, setExportResult] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageCount: 1,
    size: 20,
    count: 0,
    total: 0,
  });

  const [filters, setFilters] = useState({
    searchQuery: '',
    dateFrom: '',
    dateTo: '',
  });

  const fetchShoppers = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);

    try {
      const requestParams = {
        page: params.page || 1,
        size: params.size || 20,
        order: params.order || 'created_at DESC',
      };

      if (params.keyword) {
        requestParams.keyword = params.keyword;
      }

      if (params.dateFrom && params.dateTo) {
        requestParams.dateFrom = params.dateFrom;
        requestParams.dateTo = params.dateTo;
      }

      const response = await shopperService.getFormattedShopperList(
        requestParams
      );

      if (response && response.data) {
        setShoppers(response.data.data || []);
        setPagination({
          page: response.data.page || 1,
          pageCount: response.data.pageCount || 1,
          size: response.data.size || 20,
          count: response.data.count || 0,
          total: response.data.total || 0,
        });
      }
    } catch (err) {
      console.error('Failed to fetch shoppers:', err);
      setShoppers([]);
      setPagination(prev => ({ ...prev, page: 1, pageCount: 1, count: 0 }));

      if (err instanceof ApiError) {
        setError(`Failed to load shoppers: ${err.message}`);
      } else {
        setError('Failed to load shoppers. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearchChange = useCallback(e => {
    const value = e.target.value;
    setSearchTerm(value);
    setFilters(prev => ({
      ...prev,
      searchQuery: value.length >= 3 ? value : '',
    }));
  }, []);

  const handleSearchFocus = useCallback(() => setSearchFocused(true), []);
  const handleSearchBlur = useCallback(() => setSearchFocused(false), []);

  const applyFilters = useCallback(async () => {
    const hasSearchQuery =
      filters.searchQuery && filters.searchQuery.length >= 3;
    const hasDateRange = filters.dateFrom && filters.dateTo;

    await fetchShoppers({
      page: 1,
      size: pagination.size,
      keyword: hasSearchQuery ? filters.searchQuery : undefined,
      dateFrom: hasDateRange ? filters.dateFrom : undefined,
      dateTo: hasDateRange ? filters.dateTo : undefined,
    });
  }, [
    fetchShoppers,
    filters.dateFrom,
    filters.dateTo,
    filters.searchQuery,
    pagination.size,
  ]);

  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    setFilters({
      searchQuery: '',
      dateFrom: '',
      dateTo: '',
    });
    fetchShoppers({ page: 1, size: pagination.size });
  }, [fetchShoppers, pagination.size]);

  const handleDateRangeChange = useCallback((start, end) => {
    setFilters(prev => ({
      ...prev,
      dateFrom: start,
      dateTo: end,
    }));
  }, []);

  const handlePageChange = useCallback(
    newPage => {
      if (
        newPage < 1 ||
        newPage > pagination.pageCount ||
        newPage === pagination.page
      ) {
        return;
      }

      const hasSearchQuery =
        filters.searchQuery && filters.searchQuery.length >= 3;
      const hasDateRange = filters.dateFrom && filters.dateTo;

      fetchShoppers({
        page: newPage,
        size: pagination.size,
        keyword: hasSearchQuery ? filters.searchQuery : undefined,
        dateFrom: hasDateRange ? filters.dateFrom : undefined,
        dateTo: hasDateRange ? filters.dateTo : undefined,
      });
    },
    [
      fetchShoppers,
      filters.dateFrom,
      filters.dateTo,
      filters.searchQuery,
      pagination.page,
      pagination.pageCount,
      pagination.size,
    ]
  );

  const handleViewDetails = useCallback(
    shopper => {
      navigate(ROUTES.generatePath(ROUTES.SHOPPERS_DETAIL, { id: shopper.id }), {
        state: { shopper },
      });
    },
    [navigate]
  );

  const handleExport = useCallback(async () => {
    setExporting(true);
    setError(null);
    setExportResult(null);

    try {
      const requestParams = {
        order: 'created_at DESC',
      };
      const hasSearchQuery =
        filters.searchQuery && filters.searchQuery.length >= 3;
      const hasDateRange = filters.dateFrom && filters.dateTo;

      if (hasSearchQuery) {
        requestParams.keyword = filters.searchQuery;
      }

      if (hasDateRange) {
        requestParams.dateFrom = filters.dateFrom;
        requestParams.dateTo = filters.dateTo;
      }

      const response = await shopperService.exportShopperList(requestParams);
      const message =
        response.data?.message ||
        'Export request submitted. The file will be sent to your email shortly.';

      setExportResult({ message });
    } catch (err) {
      console.error('Failed to export shoppers:', err);
      setExportResult(null);

      if (err instanceof ApiError) {
        setError(`Failed to export shoppers: ${err.message}`);
      } else {
        setError('Failed to export shoppers. Please try again.');
      }
    } finally {
      setExporting(false);
    }
  }, [filters.dateFrom, filters.dateTo, filters.searchQuery]);

  useEffect(() => {
    if (hasInitialLoadRef.current) return;
    hasInitialLoadRef.current = true;
    fetchShoppers({ page: 1, size: 20 });
  }, [fetchShoppers]);

  useEffect(() => {
    handleDateRangeChange(startDate, endDate);
  }, [endDate, handleDateRangeChange, startDate]);

  useEffect(() => {
    if ((error || exportResult) && messageBannerRef.current) {
      messageBannerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [error, exportResult]);

  const displayStart =
    pagination.count > 0 ? (pagination.page - 1) * pagination.size + 1 : 0;
  const displayEnd =
    pagination.count > 0
      ? Math.min(pagination.page * pagination.size, pagination.count)
      : 0;
  const pageNumbers = Array.from(
    new Set(
      [1, pagination.page - 1, pagination.page, pagination.page + 1, pagination.pageCount]
        .filter(page => page >= 1 && page <= pagination.pageCount)
    )
  );

  return (
    <div>
      <PageHeader
        title='Shoppers Management'
        subtitle='Manage your shopper team and their activities'
        breadcrumbs={[
          { name: 'Home', href: ROUTES.DASHBOARD },
          { name: 'Shoppers' },
        ]}
      />

      <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6 mb-6'>
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
          <div className='relative'>
            <label
              htmlFor='search'
              className='block text-sm font-medium text-gray-700 mb-2'
            >
              Search Shoppers
            </label>
            <div className='relative'>
              <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                <svg
                  className='h-5 w-5 text-gray-400'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                  />
                </svg>
              </div>
              <input
                type='text'
                id='search'
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                placeholder='Search by name or email (min 3 chars)...'
                className={`block w-full pl-10 pr-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  searchFocused ? 'border-blue-300' : 'border-gray-300'
                } ${
                  searchTerm.length > 0 && searchTerm.length < 3
                    ? 'border-yellow-300 bg-yellow-50'
                    : ''
                }`}
              />
              {searchTerm.length > 0 && searchTerm.length < 3 && (
                <div className='absolute right-3 top-2'>
                  <div className='bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded'>
                    {3 - searchTerm.length} more
                  </div>
                </div>
              )}
            </div>
          </div>

          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            placeholder='Select join date range'
            label={{
              start: 'Join Date From',
              end: 'Join Date To',
            }}
          />

          <div className='flex flex-col justify-end md:col-span-2 lg:col-span-1'>
            <div className='flex flex-col sm:flex-row gap-2'>
              <button
                type='button'
                onClick={clearFilters}
                disabled={loading}
                className='flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-60'
              >
                Clear Filters
              </button>
              <button
                type='button'
                onClick={applyFilters}
                disabled={loading}
                className='flex-1 bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60'
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error & Export Result Display */}
      {(error || exportResult) && (
        <div ref={messageBannerRef} className='mb-6'>
          {error && (
            <div className='bg-red-50 border border-red-200 rounded-lg p-4 mb-6'>
              <div className='flex items-center'>
                <svg
                  className='w-5 h-5 text-red-400 mr-2'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
                  />
                </svg>
                <p className='text-sm text-red-700'>{error}</p>
                <button
                  onClick={() => setError(null)}
                  className='ml-auto text-red-400 hover:text-red-600'
                >
                  <svg
                    className='w-4 h-4'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M6 18L18 6M6 6l12 12'
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}
          {exportResult && (
            <div className='bg-green-50 border border-green-200 rounded-lg p-4 mb-6'>
              <div className='flex items-start'>
                <svg
                  className='w-5 h-5 text-green-400 mr-2 mt-0.5 flex-shrink-0'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                  />
                </svg>
                <div className='flex-1 min-w-0'>
                  <h4 className='text-sm font-semibold text-green-800 mb-1'>
                    Export Successful
                  </h4>
                  <p className='text-sm text-green-700'>
                    {exportResult.message}
                  </p>
                </div>
                <button
                  onClick={() => setExportResult(null)}
                  className='ml-auto text-green-400 hover:text-green-600'
                >
                  <svg
                    className='w-4 h-4'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M6 18L18 6M6 6l12 12'
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className='bg-white rounded-lg shadow-sm border border-gray-200'>
        <div className='px-4 md:px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3'>
          <div>
            <h3 className='text-lg font-medium text-gray-900'>Shoppers List</h3>
            <p className='text-sm text-gray-500 mt-1'>
              Showing {displayStart} to {displayEnd} of {pagination.count}{' '}
              shoppers
            </p>
          </div>
          <button
            type='button'
            onClick={handleExport}
            disabled={exporting || loading}
            className='bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed'
          >
            {exporting ? 'Exporting...' : 'Export Data'}
          </button>
        </div>

        <div className='overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='hidden lg:table-cell px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32'>
                  ID
                </th>
                <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Name
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Last Name
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Status
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Join Date
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Referral Code
                </th>
                <th className='px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Total LP
                </th>
                <th className='px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Cashback Balance
                </th>
                <th className='px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Contributed VP
                </th>
                <th className='px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Direct Partner
                </th>
                <th className='px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Downline
                </th>
                <th className='px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Action
                </th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200'>
              {loading && shoppers.length === 0 && (
                <tr>
                  <td colSpan={12} className='px-4 py-8 text-center text-sm text-gray-500'>
                    Loading shoppers...
                  </td>
                </tr>
              )}

              {!loading && shoppers.length === 0 && (
                <tr>
                  <td colSpan={12} className='px-4 py-8 text-center text-sm text-gray-500'>
                    No shoppers found.
                  </td>
                </tr>
              )}

              {shoppers.map(shopper => {
                const isPlus = shopper.status === 'Plus';
                return (
                  <tr key={shopper.id} className='hover:bg-gray-50'>
                    <td className='hidden lg:table-cell px-3 py-4 text-sm text-gray-900 font-mono align-top'>
                      <div
                        className='max-w-[160px] break-words'
                        title={shopper.id}
                      >
                        {shopper.id}
                      </div>
                    </td>
                    <td className='px-4 py-4 whitespace-nowrap'>
                      <div className='text-sm font-medium text-gray-900'>
                        {shopper.firstName}
                      </div>
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900'>
                      {shopper.lastName}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap'>
                      <span
                        className={`inline-flex px-2 py-1 text-sm font-semibold rounded-full ${
                          isPlus
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {shopper.status}
                      </span>
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900'>
                      {shopper.joinDate}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-xs text-gray-900 font-mono'>
                      <div title={shopper.referralCode}>
                        {shopper.referralCode}
                      </div>
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold'>
                      {formatNumber(shopper.loyaltyPoint)}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold'>
                      {formatNumber(shopper.balance)}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold'>
                      {formatNumber(shopper.contributedVp)}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold'>
                      {formatNumber(shopper.partner)}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-sm text-gray-900 text-center'>
                      {formatNumber(shopper.downline)}
                    </td>
                    <td className='px-3 py-4 whitespace-nowrap text-center'>
                      <button
                        type='button'
                        onClick={() => handleViewDetails(shopper)}
                        className='text-blue-600 hover:text-blue-900 text-sm font-medium'
                      >
                        Detail
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className='px-4 md:px-6 py-4 border-t border-gray-200 flex items-center justify-between'>
          <p className='text-sm text-gray-700'>
            Page <span className='font-medium'>{pagination.page}</span> of{' '}
            <span className='font-medium'>{pagination.pageCount}</span>
          </p>
          <nav
            className='relative z-0 inline-flex rounded-md shadow-sm -space-x-px'
            aria-label='Pagination'
          >
            <button
              type='button'
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={loading || pagination.page <= 1}
              className='relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50'
            >
              <svg
                className='h-5 w-5'
                fill='currentColor'
                viewBox='0 0 20 20'
              >
                <path
                  fillRule='evenodd'
                  d='M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z'
                  clipRule='evenodd'
                />
              </svg>
            </button>
            {pageNumbers.map(num => (
              <button
                key={num}
                type='button'
                onClick={() => handlePageChange(num)}
                disabled={loading}
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                  num === pagination.page
                    ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                } disabled:opacity-50`}
              >
                {num}
              </button>
            ))}
            <button
              type='button'
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={loading || pagination.page >= pagination.pageCount}
              className='relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50'
            >
              <svg
                className='h-5 w-5'
                fill='currentColor'
                viewBox='0 0 20 20'
              >
                <path
                  fillRule='evenodd'
                  d='M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z'
                  clipRule='evenodd'
                />
              </svg>
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default ShoppersPage;
